import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Tabs,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  UserOutlined, CarOutlined, MailOutlined, PhoneOutlined, AuditOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import TableExportButton from '../../components/TableExportButton';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import CBModal from '../../components/CBModal';
import PageHeader from '../../components/PageHeader';
import { useTauxRetenue } from '../../services/tauxRetenueService';
import { useTranslation } from 'react-i18next';
import { libellesTraduits } from '../../i18n/libelles';
import MontantDefaut from './MontantDefaut';
import TauxDeRetenue from '../comptabilite/TauxDeRetenue';
const TYPE_LABELS = libellesTraduits('experts:types');
/* DES ICONES, ET NON DES EMOJIS. Le tableau portait deja `UserOutlined` et
   `CarOutlined` dans son tag de type ; les deux listes de choix affichaient des
   emojis - deux symboles pour la meme chose, et des emojis dont le dessin depend
   du systeme de l'utilisateur. La source est desormais unique, et les trois
   endroits ne peuvent plus diverger. */
const TYPE_ICONES = { MEDICAL: <UserOutlined />, AUTOMOBILE: <CarOutlined /> };
// Construite au rendu : TYPE_LABELS se traduit a la lecture.
const typeOptions = () => Object.keys(TYPE_LABELS).map((v) => ({
  value: v,
  label: <span>{TYPE_ICONES[v]} {TYPE_LABELS[v]}</span>,
}));


function Annuaire() {
  const { t } = useTranslation('experts');
  // Taux de retenue : référentiel paramétrable (Comptabilité → Fiscalité), plus
  // une liste figée. La liste de choix ne propose que les taux en vigueur.
  const tauxRetenue = useTauxRetenue();
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [typeFiltre, setTypeFiltre] = useState(null);
  /* LES PAYS, POUR LA NATIONALITE. Le serveur accepte `paysTrackingId` depuis
     toujours ; le formulaire n'offrait pas le champ, et la nationalite d'un expert
     ne pouvait donc jamais etre renseignee. Chargee une fois : la liste est courte
     et ne bouge qu'au parametrage. */
  const [pays, setPays] = useState([]);
  const [form] = Form.useForm();

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const types = typeFiltre ? [typeFiltre] : ['MEDICAL', 'AUTOMOBILE'];
      const all = await Promise.all(
        types.map(t => axiosInstance.get(`/v1/experts/type/${t}`))
      );
      setExperts(all.flatMap(r => r.data?.data || []));
    } catch { toast.error(t('messages.erreurChargement')); }
    setLoading(false);
  }, [typeFiltre, t]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    axiosInstance.get('/v1/pays')
      // Le selecteur reste vide : creer un expert sans nationalite demeure possible,
      // et un toast de plus sur un ecran qui s'ouvre n'aide personne.
      .then((r) => setPays(r.data?.data || []))
      .catch(() => setPays([]));
  }, []);

  const ouvrir = (expert = null) => {
    setEditing(expert);
    form.setFieldsValue(expert
      ? { ...expert, actif: expert.actif }
      : { typeExpert: 'MEDICAL', actif: true }
    );
    setModal(true);
  };

  const soumettre = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        await axiosInstance.put(`/v1/experts/${editing.expertTrackingId}`, values);
        toast.success(t('messages.misAJour'));
      } else {
        await axiosInstance.post('/v1/experts', values);
        toast.success(t('messages.cree'));
      }
      setModal(false);
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('messages.erreurSauvegarde'));
    }
    setSaving(false);
  };

  const supprimer = async (id) => {
    try {
      await axiosInstance.delete(`/v1/experts/${id}`);
      toast.success(t('messages.supprime'));
      charger();
    } catch { toast.error(t('messages.erreurSuppression')); }
  };

  const columns = [
    // Numero d'ordre dans la page affichee : repere de lecture pour designer une
    // ligne. Hors export, la numerotation n'a pas de sens dans un fichier qu'on retrie.
    {
      title: t('colonnes.ordre'), key: 'ordre', width: 56, align: 'center', exportable: false,
      render: (_, __, index) => <span style={{ fontWeight: 700, color: '#94a3b8' }}>{index + 1}</span>,
    },
    {
      title: t('colonnes.type'), dataIndex: 'typeExpert', width: 110,
      render: t => (
        <Tag icon={TYPE_ICONES[t]}
          color={t === 'MEDICAL' ? 'blue' : 'orange'}>
          {TYPE_LABELS[t]}
        </Tag>
      ),
      export: (v) => TYPE_LABELS[v] || v || '',
    },
    { title: t('colonnes.nomComplet'), dataIndex: 'nomComplet', sorter: (a, b) => a.nomComplet.localeCompare(b.nomComplet) },
    { title: t('colonnes.specialite'), dataIndex: 'specialite', render: v => v || '-', export: (v) => v || '' },
    {
      title: t('colonnes.contact'), key: 'contact',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {r.email && <span style={{ fontSize: 12 }}><MailOutlined /> {r.email}</span>}
          {r.telephone && <span style={{ fontSize: 12 }}><PhoneOutlined /> {r.telephone}</span>}
          {!r.email && !r.telephone && <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('colonnes.nonRenseigne')}</span>}
        </Space>
      ),
      export: (_, r) => [r.email, r.telephone].filter(Boolean).join(' / '),
    },
    { title: t('colonnes.nif'), dataIndex: 'nif', render: v => v || '-', export: (v) => v || '' },
    {
      // `tauxRetenue` porte un CODE du référentiel (« CINQ_POURCENT »), jamais un
      // nombre : on affiche sa traduction (« 5 % »), comme partout ailleurs.
      title: t('colonnes.retenue'), dataIndex: 'tauxRetenue',
      render: (v) => tauxRetenue.affichage(v),
      export: (v) => (v ? tauxRetenue.affichage(v) : ''),
    },
    {
      title: t('colonnes.statut'), dataIndex: 'actif', width: 90, format: 'bool',
      render: v => <Tag color={v ? 'green' : 'red'}>{v ? t('statuts.actif') : t('statuts.inactif')}</Tag>,
    },
    {
      title: t('colonnes.pays'), dataIndex: 'paysLibelle', render: v => v || '-', export: (v) => v || '',
    },
    {
      title: '', key: 'actions', width: 90, exportable: false,
      render: (_, r) => (
        <Space>
          <Tooltip title={t('actions.modifier')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => ouvrir(r)} />
          </Tooltip>
          <Popconfirm title={t('actions.confirmerSuppression')} onConfirm={() => supprimer(r.expertTrackingId)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200 }}>
        {/* En-tête */}
        {/* L'API renvoie la liste complete des types demandes (la pagination est
            cliente) : `experts.length` est donc bien le total, filtre compris. */}
        <PageHeader
          titre={t('titre')}
          icone={<AuditOutlined />}
          compte={experts.length}
          unite={t('unite')}
          actions={(
            <Space>
              <Select
                placeholder={t('filtres.tousTypes')}
                allowClear
                style={{ width: 160 }}
                onChange={setTypeFiltre}
                options={typeOptions()}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={() => ouvrir()}>
                {t('actions.nouveau')}
              </Button>
            </Space>
          )}
        />

        <Table
          dataSource={experts}
          columns={columns}
          rowKey="expertTrackingId"
          loading={loading}
          size="small"
          style={{ marginBottom: 80 }}
          pagination={{ pageSize: 20 }}
        />

        <TableExportButton
          floating
          data={experts}
          columns={columns}
          filename="experts"
          title={t('titre')}
          subtitle={typeFiltre ? t('export.type', { type: TYPE_LABELS[typeFiltre] || typeFiltre }) : t('export.tous')}
          count={experts.length}
        />

        {/* Modal création / édition */}
        <CBModal
          open={modal}
          title={editing ? t('modale.titreModification') : t('actions.nouveau')}
          onCancel={() => setModal(false)}
          footer={null}
          width={640}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={soumettre} style={{ marginTop: 16 }}>
            <Form.Item name="typeExpert" label={t('modale.type')} rules={[{ required: true }]}>
              <Select options={typeOptions()} />
            </Form.Item>

            <Form.Item name="nomComplet" label={t('colonnes.nomComplet')} rules={[{ required: true }]}>
              <Input placeholder={t('modale.nomPlaceholder')} />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Item name="specialite" label={t('colonnes.specialite')}>
                <Input placeholder={t('modale.specialitePlaceholder')} />
              </Form.Item>
              <Form.Item name="nif" label={t('colonnes.nif')}>
                <Input placeholder={t('modale.nifPlaceholder')} />
              </Form.Item>
            </div>

            {/* `paysTrackingId` et non un libelle : c'est ce que la requete attend, et
                c'est ce que la reponse rend desormais - sans quoi modifier un expert
                aurait efface sa nationalite, le champ partant vide. */}
            <Form.Item name="paysTrackingId" label={t('modale.nationalite')}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('modale.choisirPays')}
                options={pays.map((x) => ({ label: x.libelle, value: x.paysTrackingId }))}
              />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Item name="email" label={t('modale.email')} rules={[{ type: 'email', message: t('modale.emailInvalide') }]}>
                <Input prefix={<MailOutlined />} placeholder="expert@domaine.com" />
              </Form.Item>
              <Form.Item name="telephone" label={t('modale.telephone')}>
                <Input prefix={<PhoneOutlined />} placeholder="+228 XX XX XX XX" />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Item name="tauxRetenue" label={t('modale.tauxRetenue')}
                extra={t('modale.tauxRetenueAide')}>
                {/* Liste des taux en vigueur, pas une saisie libre. Laissé vide, le
                    serveur applique le taux par défaut. */}
                <Select
                  allowClear
                  placeholder={t('modale.tauxDefaut')}
                  options={tauxRetenue.options}
                />
              </Form.Item>
              <Form.Item name="actif" label={t('statuts.actif')} valuePropName="checked">
                <Switch checkedChildren={t('statuts.actif')} unCheckedChildren={t('statuts.inactif')} />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <Button onClick={() => setModal(false)}>{t('actions.annuler')}</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editing ? t('actions.mettreAJour') : t('actions.creer')}
              </Button>
            </div>
          </Form>
      </CBModal>
    </div>
  );
}

/* LA PAGE : L'ANNUAIRE, ET LE PARAMETRAGE QUI LE GOUVERNE.

   Les deux reglages qui decident de ce qu'on saisit sur une fiche d'expert vivaient
   ailleurs. Le montant par defaut n'existait nulle part - il etait en dur, en deux
   exemplaires contradictoires. Le referentiel des taux de retenue etait un onglet de
   la page Fiscalite, a cote d'un etat comptable : deux choses de nature differente,
   l'une qu'on consulte, l'autre qu'on modifie. Un taux de retenue ne s'applique qu'aux
   honoraires d'experts ; sa place est ici.

   `destroyInactiveTabPane` n'est pas un detail : l'annuaire porte un bouton d'export
   flottant (`position: fixed`), et deux panneaux montes en meme temps empileraient
   deux boutons dans le meme coin. */
export default function Experts() {
  const { t } = useTranslation('experts');
  return (
    <Layout title={t('titre')}>
      <Tabs
        destroyInactiveTabPane
        items={[
          { key: 'annuaire', label: t('onglets.annuaire'), children: <Annuaire /> },
          {
            key: 'parametrage',
            label: t('onglets.parametrage'),
            children: (
              <div style={{ display: 'grid', gap: 16, maxWidth: 1200 }}>
                <div className="cb-card">
                  <div className="cb-card-title" style={{ marginBottom: 12 }}>
                    {t('parametrage.montantExpertise')}
                  </div>
                  <MontantDefaut />
                </div>
                <div className="cb-card">
                  <div className="cb-card-title" style={{ marginBottom: 12 }}>
                    {t('parametrage.tauxRetenue')}
                  </div>
                  <TauxDeRetenue />
                </div>
              </div>
            ),
          },
        ]}
      />
    </Layout>
  );
}
